import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@wds/box';
import { IconCheck } from '@sm/wds-icons';
import { Button, Typography } from '@sm/wds-react';
import { clientErrorHandler } from '@sm/webassets';
import { generateMetricsAttribute } from '@sm/metrics';
import { saveFormData } from '../../../components/HubSpotForm/helpers';
import useHubSpotFormAPI from '~pricing/hooks/useHubSpotAPI';
import { buildHubspotPayload, cleanUpFormFields } from '../helpers';
import NotifyInterestModal from './NotifyInterestModal';
import useStyles from '../useStyles';
import COPY from '../assets/EnterpriseFeaturesCopy';

const EXPERT_SERVICES_PROGRAM_ID = '7014V000002dEzdQAE';
export default function ExpertServices({ user }) {
  const {
    expertServicesPanel,
    centerText,
    lightTitle,
    lightBody,
    bannerButton,
    row,
    chimeBox,
    colgateBox,
    pumaBox,
    checkMark,
  } = useStyles();
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { data } = useHubSpotFormAPI('1a594719-3256-4c2f-b2ea-06fcb43de45f');
  const formFields = useMemo(() => data?.fields, [data?.fields]);
  const expertServicesInterest = localStorage.getItem('pnp-notify-interest-expert-services');

  const toggleThankYouModal = open => {
    setShowThankYouModal(open);
  };

  const notifyInterest = async programId => {
    const autoFilledFormData = buildHubspotPayload(user);
    const hubspotUrlRoute = `${data?.portalId}/${data?.guid}`;

    try {
      const res = await saveFormData(
        { formFields: cleanUpFormFields(programId, formFields), formData: autoFilledFormData },
        true,
        hubspotUrlRoute
      );
      if (!res?.status?.errors) {
        setSubmitted(true);
        toggleThankYouModal(true);
      }
    } catch (err) {
      clientErrorHandler.logError(err, 'Error notifying sales team of interest.');
    }
  };

  return (
    <>
      <Box className={expertServicesPanel} flexDirection="column" flexJustify="center">
        <Typography variant="pageTitle" className={`${lightTitle} ${centerText}`}>
          Expert Services
        </Typography>
        <Typography variant="body" className={`${lightBody} ${centerText}`}>
          We offer 1:1 expert services support for optimizing your survey programming. Connect with our research
          professionals who can guide you or manage your project end-to-end.
        </Typography>
        <Box className={row} display="flex" flexDirection="row" flexJustify="center">
          <Button
            variant="solid"
            color="alt"
            className={bannerButton}
            disabled={submitted || expertServicesInterest}
            onClick={() => {
              notifyInterest(EXPERT_SERVICES_PROGRAM_ID);
              localStorage.setItem('pnp-notify-interest-expert-services', true);
            }}
            data-sm-metrics={generateMetricsAttribute({
              data: {
                desiredProduct: 'expert services',
                amplitudeEvent: 'pricing clicked enterprise cta',
              },
            })}
          >
            {submitted || expertServicesInterest ? <IconCheck className={checkMark} size="xs" /> : <></>}
            {submitted || expertServicesInterest ? 'Interest Sent' : 'Notify Interest'}
          </Button>
        </Box>

        <Box className={row} display="flex" flexDirection="row" flexJustify="center">
          <TestimonialCard testimonial={COPY.pumaTestimonial} cssClass={pumaBox} cardColor="#902B3C" />
          <TestimonialCard testimonial={COPY.chimeTestimonial} cssClass={chimeBox} cardColor="#53225B" />
          <TestimonialCard testimonial={COPY.colgateTestimonial} cssClass={colgateBox} cardColor="#051C4F" />
        </Box>
        <NotifyInterestModal showModal={showThankYouModal} toggleModal={toggleThankYouModal} />
      </Box>
    </>
  );
}

function TestimonialCard({ testimonial, cssClass, cardColor }) {
  const { testimonialCard, testimonialBody, quoteBox } = useStyles();
  const { logo, body, quottedPerson, company } = testimonial;
  const { img, height, width } = logo;

  return (
    <>
      {/* Border color depends on card so it's done inline  */}
      <Box display="flex" flexDirection="column" className={testimonialCard}>
        <Box display="flex" flexDirection="column" className={cssClass}>
          <img src={img} height={height} width={width} className={logo} alt={company} />
          <Typography variant="body" style={{ color: cardColor }} className={testimonialBody}>
            {body}
          </Typography>
        </Box>
        <div className={quoteBox}>
          <Typography variant="body" style={{ color: cardColor, fontWeight: 500 }}>
            {quottedPerson}
          </Typography>
          <Typography variant="body" style={{ color: cardColor, fontWeight: 500 }}>
            {company}
          </Typography>
        </div>
      </Box>
    </>
  );
}

ExpertServices.propTypes = {
  user: PropTypes.objectOf(PropTypes.object).isRequired,
};

TestimonialCard.propTypes = {
  cssClass: PropTypes.string.isRequired,
  cardColor: PropTypes.string.isRequired,
  testimonial: PropTypes.shape(
    PropTypes.shape({
      body: PropTypes.string,
      quottedPerson: PropTypes.string,
      company: PropTypes.string,
      logo: PropTypes.shape({
        img: PropTypes.node,
        height: PropTypes.string,
        width: PropTypes.string,
      }),
    })
  ).isRequired,
};
